import { format, parseISO } from 'date-fns';
import { EChartsOption } from 'echarts';

const HOUR_MS = 3600000;
const DAY_MS = 86400000;

export type VoucherRightPanelTab = 'redeemed' | 'usage';

export type VoucherExpiryTone = 'green' | 'amber' | 'red';

export function toEpochMs(value: string | Date | null | undefined): number | null {
  if (value == null || value === '') {
    return null;
  }
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function voucherTimeRemainingPercent(
  createdDate: string | Date | null | undefined,
  expiresAt: string | Date | null | undefined,
  nowMs: number,
): number | null {
  const start = toEpochMs(createdDate);
  const end = toEpochMs(expiresAt);
  if (start == null || end == null) {
    return null;
  }

  const windowMs = end - start;
  const remainingMs = end - nowMs;
  if (remainingMs <= 0) {
    return 0;
  }
  if (windowMs <= 0) {
    return 100;
  }

  return Math.max(0, Math.min(100, (remainingMs / windowMs) * 100));
}

export function voucherExpiryTone(percentRemaining: number | null): VoucherExpiryTone {
  const remaining = percentRemaining ?? 0;
  if (remaining <= 30) {
    return 'red';
  }
  if (remaining <= 60) {
    return 'amber';
  }
  return 'green';
}

export function voucherExpiryFillClass(percentRemaining: number | null): string {
  const tone = voucherExpiryTone(percentRemaining);
  if (tone === 'green') {
    return 'application-progress-bar-inner bg-green-500';
  }
  if (tone === 'amber') {
    return 'application-progress-bar-inner bg-amber-500';
  }
  return 'application-progress-bar-inner bg-red-400';
}

export function formatCompactDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) {
    return '0m';
  }

  const days = Math.floor(ms / DAY_MS);
  const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
  const minutes = Math.floor((ms % HOUR_MS) / 60000);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes}m`;
}

export function voucherTimeLeftLabel(
  expiresAt: string | Date | null | undefined,
  nowMs: number,
): string {
  const end = toEpochMs(expiresAt);
  if (end == null) {
    return 'Expired';
  }
  const remaining = end - nowMs;
  if (remaining <= 0) {
    return 'Expired';
  }
  return `${formatCompactDurationMs(remaining)} left`;
}

export function utcDateKey(value: Date): string {
  const y = value.getUTCFullYear();
  const m = `${value.getUTCMonth() + 1}`.padStart(2, '0');
  const d = `${value.getUTCDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function eachUtcDateKey(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
): string[] {
  const startMs = toEpochMs(start);
  const endMs = toEpochMs(end);
  if (startMs == null || endMs == null) {
    return [];
  }

  const startDate = new Date(startMs);
  const endDate = new Date(endMs);
  let cursor = Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate());
  const last = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate());
  if (cursor > last) {
    return [];
  }

  const keys: string[] = [];
  while (cursor <= last) {
    keys.push(utcDateKey(new Date(cursor)));
    cursor += DAY_MS;
  }
  return keys;
}

export function buildVoucherUsagePoints(
  createdDate: string | Date | null | undefined,
  expiresAt: string | Date | null | undefined,
  redemptions: { redeemedAt: string | Date }[],
): { date: string; value: number }[] {
  const keys = eachUtcDateKey(createdDate, expiresAt);
  const counts = new Map(keys.map((key) => [key, 0]));

  for (const row of redemptions) {
    const ms = toEpochMs(row.redeemedAt);
    if (ms == null) {
      continue;
    }
    const key = utcDateKey(new Date(ms));
    if (counts.has(key)) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  return keys.map((date) => ({ date, value: counts.get(date) ?? 0 }));
}

export function buildVoucherUsageChart(points: { date: string; value: number }[]): {
  chartOption: EChartsOption;
  chartXaxisData: string[];
  hasData: boolean;
} {
  const chartXaxisData = points.map((point) => format(parseISO(point.date), 'd MMM'));
  const chartOption: EChartsOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '3%', bottom: '12%', top: '8%', containLabel: true },
    xAxis: { type: 'category', data: chartXaxisData },
    yAxis: { type: 'value', minInterval: 1 },
    series: [
      {
        name: 'Redemptions',
        type: 'bar',
        data: points.map((point) => point.value),
      },
    ],
  };

  return {
    chartOption,
    chartXaxisData,
    hasData: points.some((point) => point.value > 0),
  };
}
